/// Axios client với JWT auto-attach + 401 refresh-retry pattern.
/// Reference: evidence-srs.md Evidence #13.
///
/// Pattern: Pinia auth store + Flag+Queue interceptor.
/// Single in-flight refresh prevents concurrent /auth/refresh calls.

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import { useAuthStore } from '@/features/auth/stores/auth-store'

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

// Refresh client — KHÔNG có interceptor, dùng riêng cho POST /auth/refresh
// (tránh circular: refresh call cũng bị wrap → infinite loop khi 401).
// withCredentials: gửi HttpOnly refresh cookie (R2). Server đọc cookie ?? body.
export const refreshClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 10_000,
  withCredentials: true,
})

// Legacy refresh token (trước migration R2) — chỉ dùng 1 lần để bootstrap cookie
// cho user đang đăng nhập lúc deploy (tránh force-logout). Sau đó xóa khỏi localStorage.
const LEGACY_REFRESH_TOKEN_KEY = 'tts.refresh_token'

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

interface QueuedRequest {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
  config: InternalAxiosRequestConfig
}

let isRefreshing = false
let pendingQueue: QueuedRequest[] = []

// Access-whitelist block codes (AUT-whitelist). A 403 carrying one of these on a
// normal request means the company enabled blocking and this session's IP/device
// is no longer allowed — bounce to login with the reason (not a silent toast).
const ACCESS_BLOCK_CODES = new Set(['IP_NOT_ALLOWED', 'DEVICE_NOT_WHITELISTED'])

// Mã mà /auth/refresh dùng để nói "phiên này KHÔNG cứu được nữa". Cùng một ý nhưng về
// với ba status khác nhau: 401 (token đã revoke/hết hạn trong DB), 422 (trình duyệt không
// còn gửi cookie refresh — ValidationError phía BE), 403 (CSRF_INVALID). Quyết theo
// status thì hai ca sau rơi vào nhánh "tạm thời": giữ session, trang hiện "session
// invalid" + Retry, bấm Retry lại đúng vòng đó — kẹt vĩnh viễn, không bao giờ về login.
// Quyết theo `code` là theo đúng hợp đồng lỗi (code là cái BE cam kết, status thì không).
const SESSION_DEAD_CODES = new Set(['AUTH_REFRESH_INVALID', 'CSRF_INVALID'])

function errorCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined
  return (err.response?.data as { code?: string } | undefined)?.code
}

/** /auth/refresh từ chối DỨT KHOÁT — khác với lỗi tạm thời (mạng, 5xx, 409 tab khác đang rotate). */
function isSessionDead(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status === 401) return true
  const code = errorCode(err)
  return code != null && SESSION_DEAD_CODES.has(code)
}

// Redirect về màn login khi session hết hạn. Dynamic import router để tránh
// circular dependency (router → auth-store → axios-client). Giữ lại query
// `redirect` để quay lại trang đang xem sau khi đăng nhập lại.
//
// Chờ `router.isReady()` trước khi đọc currentRoute: khi F5, App.vue gọi API ngay trong
// onMounted (hydrate system-config) — lúc đó điều hướng đầu tiên CHƯA xong, currentRoute
// vẫn là START_LOCATION ('/'), nên `redirect` ghi '/' thay vì trang đang xem. Đo thật
// trên dev stack 2026-09-22: về `/login?redirect=/` dù F5 từ /alert-history.
//
// `isReady()` REJECT khi điều hướng đầu tiên lỗi (vue-router `triggerError` →
// `markAsReady(err)`, và `ready` giữ false mãi) — ca thật: F5 sau deploy, chunk cũ 404.
// Không có fallback thì phiên đã bị xoá nhưng không bao giờ push login, kèm một
// unhandled rejection bị error-reporter báo lên server. Khi đó không còn trang nào để
// quay lại → về login trơn, không `redirect`.
function redirectToLogin() {
  void import('@/core/router').then(async ({ router }) => {
    try {
      await router.isReady()
    } catch {
      void router.push({ name: 'login' })
      return
    }
    if (router.currentRoute.value.name === 'login') return
    void router.push({
      name: 'login',
      query: { redirect: router.currentRoute.value.fullPath },
    })
  })
}

function flushQueue(error: Error | null, accessToken: string | null) {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error || !accessToken) {
      reject(error)
      return
    }
    config.headers.set('Authorization', `Bearer ${accessToken}`)
    resolve(apiClient(config))
  })
  pendingQueue = []
}

// ── Request interceptor: inject Bearer token ──────────────────────────────
apiClient.interceptors.request.use((config) => {
  const auth = useAuthStore()
  if (auth.accessToken) {
    config.headers.set('Authorization', `Bearer ${auth.accessToken}`)
  }
  // Locale routing (server uses Accept-Language)
  config.headers.set('Accept-Language', auth.locale ?? 'ja')
  return config
})

// ── Response interceptor: 401 → refresh + retry ───────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined

    // Mid-session access-whitelist block (IP/device): even though the user is
    // signed in, this request was denied because the identifier is outside the
    // company allowlist. Drop the session and redirect to login showing the
    // reason + identifier (mirrors the login-time block) — FR-014 / AUT-whitelist.
    const blockCode = errorCode(error)
    if (
      error.response?.status === 403 &&
      blockCode &&
      ACCESS_BLOCK_CODES.has(blockCode)
    ) {
      useAuthStore().flagAccessBlocked(error)
      redirectToLogin()
      return Promise.reject(error)
    }

    if (
      !originalConfig ||
      error.response?.status !== 401 ||
      originalConfig._retry ||
      originalConfig.url?.endsWith('/auth/refresh') ||
      originalConfig.url?.endsWith('/auth/login')
    ) {
      return Promise.reject(error)
    }

    // Queue request nếu đang refresh
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject, config: originalConfig })
      })
    }

    originalConfig._retry = true
    isRefreshing = true

    const auth = useAuthStore()
    try {
      // R2: refresh token nằm trong HttpOnly cookie (gửi tự động qua withCredentials).
      // Bootstrap migration: user cũ còn token trong localStorage → gửi 1 lần qua body
      // để server cấp lại cookie, rồi xóa khỏi localStorage (tránh force-logout).
      const legacy =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY)
          : null
      const body = legacy ? { refreshToken: legacy } : {}

      const resp = await refreshClient.post<{
        accessToken: string
        refreshToken?: string
      }>('/auth/refresh', body)

      if (legacy && typeof localStorage !== 'undefined') {
        localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY)
      }

      // Web: response KHÔNG có refreshToken (đã ở cookie) → setTokens chỉ lưu access.
      auth.setTokens(resp.data.accessToken, resp.data.refreshToken)
      flushQueue(null, resp.data.accessToken)

      originalConfig.headers.set(
        'Authorization',
        `Bearer ${resp.data.accessToken}`,
      )
      return apiClient(originalConfig)
    } catch (refreshErr) {
      flushQueue(refreshErr as Error, null)
      // Only a DEFINITIVE refusal (session dead / reused / cookie gone / CSRF) ends the
      // session — see `isSessionDead`. Offline, 5xx, or 409 AUTH_REFRESH_IN_PROGRESS
      // (another tab is rotating the cookie right now) are transient: keep the session,
      // fail this request, the next one simply tries again. Signing out on those was a
      // false "session expired".
      if (isSessionDead(refreshErr)) {
        // Về login KÈM lý do (cùng khuôn với flagAccessBlocked): bị ném ra màn đăng nhập
        // giữa lúc đang làm việc mà không một lời nào thì người dùng không biết vì sao.
        auth.flagSessionExpired(refreshErr)
        // Lazy import router để tránh circular dependency (router import store/guard).
        redirectToLogin()
      }
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

export function request<T>(config: AxiosRequestConfig): Promise<T> {
  return apiClient(config).then((r) => r.data as T)
}
